/**
 * RequisitesFormModal - Модальное окно для создания/редактирования реквизитов
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Select,
  Button,
  Group,
  Stack,
  Text,
  Alert,
  Checkbox,
  Divider,
  SegmentedControl,
  Box,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import {
  IconAlertCircle,
  IconBuildingBank,
  IconUser,
  IconCheck,
  IconMapPin,
  IconCalendar,
  IconPhone,
  IconMail,
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';

// Функции валидации
const validateInn = (inn, entityType) => {
  if (!inn) return 'ИНН обязателен';
  if (!/^\d+$/.test(inn)) return 'ИНН должен содержать только цифры';
  if (entityType === 'legal' && inn.length !== 10) {
    return 'ИНН юридического лица должен содержать 10 цифр';
  }
  if (entityType === 'individual' && inn.length !== 12) {
    return 'ИНН ИП должен содержать 12 цифр';
  }
  return null;
};

const validateKpp = (kpp, entityType) => {
  if (entityType === 'individual') return null; // КПП не нужен для ИП
  if (!kpp) return 'КПП обязателен для юридических лиц';
  if (!/^\d{9}$/.test(kpp)) return 'КПП должен содержать 9 цифр';
  return null;
};

const validateOgrn = (ogrn, entityType) => {
  if (!ogrn) return 'ОГРН обязателен';
  if (!/^\d+$/.test(ogrn)) return 'ОГРН должен содержать только цифры';
  if (entityType === 'legal' && ogrn.length !== 13) {
    return 'ОГРН юридического лица должен содержать 13 цифр';
  }
  if (entityType === 'individual' && ogrn.length !== 15) {
    return 'ОГРНИП должен содержать 15 цифр';
  }
  return null;
};

const validateBankAccount = (account) => {
  if (!account) return 'Расчётный счёт обязателен';
  if (!/^\d{20}$/.test(account)) return 'Расчётный счёт должен содержать 20 цифр';
  return null;
};

const validateBik = (bik) => {
  if (!bik) return 'БИК обязателен';
  if (!/^\d{9}$/.test(bik)) return 'БИК должен содержать 9 цифр';
  return null;
};

const RequisitesFormModal = ({
  opened,
  onClose,
  onSubmit,
  requisites = null, // null = создание, object = редактирование
  organizationId,
  organization = null, // Для получения адреса
  contacts = [],
  loading = false,
}) => {
  const isEditing = !!requisites;

  const [formData, setFormData] = useState({
    entity_type: 'legal',
    company_name: '',
    inn: '',
    kpp: '',
    ogrn: '',
    bank_account: '',
    bank_name: '',
    bank_bik: '',
    legal_address: '',
    physical_address: '',
    director_contact_id: null,
    contact_person_id: null,
    contract_start_date: null,
    class_days: '',
    class_time: '',
    is_primary: false,
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  // Инициализация формы при открытии
  useEffect(() => {
    if (opened) {
      if (requisites) {
        setFormData({
          entity_type: requisites.entity_type || 'legal',
          company_name: requisites.company_name || '',
          inn: requisites.inn || '',
          kpp: requisites.kpp || '',
          ogrn: requisites.ogrn || '',
          bank_account: requisites.bank_account || '',
          bank_name: requisites.bank_name || '',
          bank_bik: requisites.bank_bik || '',
          legal_address: requisites.legal_address || '',
          physical_address: requisites.physical_address || '',
          director_contact_id: requisites.director_contact_id || null,
          contact_person_id: requisites.contact_person_id || null,
          contract_start_date: requisites.contract_start_date
            ? new Date(requisites.contract_start_date)
            : null,
          class_days: requisites.class_days || '',
          class_time: requisites.class_time || '',
          is_primary: requisites.is_primary || false,
        });
      } else {
        // Значения по умолчанию для новых реквизитов
        const defaultDirector = contacts.find(
          c => c.position_name === 'Генеральный директор' || c.position_name === 'Руководитель'
        );
        const defaultContact = contacts.find(c => c.is_primary) || contacts[0];

        setFormData({
          entity_type: 'legal',
          company_name: '',
          inn: '',
          kpp: '',
          ogrn: '',
          bank_account: '',
          bank_name: '',
          bank_bik: '',
          legal_address: '',
          physical_address: organization?.address || '', // Подставляем адрес организации
          director_contact_id: defaultDirector?.id || null,
          contact_person_id: defaultContact?.id || null,
          contract_start_date: null,
          class_days: '',
          class_time: '',
          is_primary: false,
        });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [opened, requisites, contacts, organization]);

  // Валидация поля в реальном времени
  const validateField = (name, value) => {
    let error = null;

    switch (name) {
      case 'inn':
        error = validateInn(value, formData.entity_type);
        break;
      case 'kpp':
        error = validateKpp(value, formData.entity_type);
        break;
      case 'ogrn':
        error = validateOgrn(value, formData.entity_type);
        break;
      case 'bank_account':
        error = validateBankAccount(value);
        break;
      case 'bank_bik':
        error = validateBik(value);
        break;
      case 'company_name':
        if (!value?.trim()) error = 'Название обязательно';
        break;
      case 'bank_name':
        if (!value?.trim()) error = 'Название банка обязательно';
        break;
      case 'legal_address':
        if (!value?.trim()) error = 'Юридический адрес обязателен';
        break;
      case 'physical_address':
        if (!value?.trim()) error = 'Физический адрес обязателен';
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [name]: error }));
    return error;
  };

  // Обработка изменения типа организации
  const handleEntityTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      entity_type: value,
      kpp: value === 'individual' ? '' : prev.kpp, // Очистка КПП для ИП
    }));
    // Сбрасываем ошибки для полей ИНН, КПП, ОГРН при смене типа
    setErrors(prev => ({ ...prev, inn: null, kpp: null, ogrn: null }));
  };

  // Обработка изменения поля
  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  // Полная валидация формы
  const validateForm = () => {
    const newErrors = {};

    // Обязательные поля
    if (!formData.company_name?.trim()) {
      newErrors.company_name = 'Название обязательно';
    }

    const innError = validateInn(formData.inn, formData.entity_type);
    if (innError) newErrors.inn = innError;

    const kppError = validateKpp(formData.kpp, formData.entity_type);
    if (kppError) newErrors.kpp = kppError;

    const ogrnError = validateOgrn(formData.ogrn, formData.entity_type);
    if (ogrnError) newErrors.ogrn = ogrnError;

    const bankAccountError = validateBankAccount(formData.bank_account);
    if (bankAccountError) newErrors.bank_account = bankAccountError;

    if (!formData.bank_name?.trim()) {
      newErrors.bank_name = 'Название банка обязательно';
    }

    const bikError = validateBik(formData.bank_bik);
    if (bikError) newErrors.bank_bik = bikError;

    if (!formData.legal_address?.trim()) {
      newErrors.legal_address = 'Юридический адрес обязателен';
    }

    if (!formData.physical_address?.trim()) {
      newErrors.physical_address = 'Физический адрес обязателен';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      const data = {
        organization_id: organizationId,
        entity_type: formData.entity_type,
        company_name: formData.company_name.trim(),
        inn: formData.inn.trim(),
        kpp: formData.entity_type === 'legal' ? formData.kpp.trim() : null,
        ogrn: formData.ogrn.trim(),
        bank_account: formData.bank_account.trim(),
        bank_name: formData.bank_name.trim(),
        bank_bik: formData.bank_bik.trim(),
        legal_address: formData.legal_address.trim(),
        physical_address: formData.physical_address.trim(),
        director_contact_id: formData.director_contact_id || null,
        contact_person_id: formData.contact_person_id || null,
        contract_start_date: formData.contract_start_date
          ? formData.contract_start_date.toISOString().split('T')[0]
          : null,
        class_days: formData.class_days?.trim() || null,
        class_time: formData.class_time?.trim() || null,
        is_primary: formData.is_primary,
      };

      await onSubmit(data);
    } catch (err) {
      setSubmitError(err.message || 'Ошибка при сохранении реквизитов');
    }
  };

  // Форматирование полного имени контакта
  const formatContactName = (contact) => {
    const parts = [];
    if (contact.last_name) parts.push(contact.last_name);
    if (contact.first_name) parts.push(contact.first_name);
    if (contact.middle_name) parts.push(contact.middle_name);
    return parts.join(' ') || 'Без имени';
  };

  // Опции для Select контактов
  const contactOptions = contacts.map(c => ({
    value: c.id.toString(),
    label: formatContactName(c) + (c.position_name ? ` (${c.position_name})` : ''),
  }));

  // Фильтрация директоров
  const directorContacts = contacts.filter(
    c => c.position_name === 'Генеральный директор' || c.position_name === 'Руководитель'
  );
  const directorOptions = directorContacts.map(c => ({
    value: c.id.toString(),
    label: formatContactName(c) + (c.position_name ? ` (${c.position_name})` : ''),
  }));

  // Получаем выбранное контактное лицо для отображения телефона и email
  const selectedContactPerson = contacts.find(c => c.id === formData.contact_person_id);

  // Форматируем телефон контакта
  const getContactPhone = (contact) => {
    if (!contact?.phones) return null;
    if (Array.isArray(contact.phones) && contact.phones.length > 0) {
      const phone = contact.phones[0];
      return typeof phone === 'object' ? phone.number : phone;
    }
    return null;
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditing ? 'Редактировать реквизиты' : 'Добавить реквизиты'}
      size="lg"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {submitError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {submitError}
            </Alert>
          )}

          {/* Тип организации */}
          <Box>
            <Text size="sm" fw={500} mb="xs">
              Тип организации
            </Text>
            <SegmentedControl
              value={formData.entity_type}
              onChange={handleEntityTypeChange}
              data={[
                {
                  value: 'legal',
                  label: (
                    <Group gap="xs">
                      <IconBuildingBank size={16} />
                      <span>Юридическое лицо</span>
                    </Group>
                  ),
                },
                {
                  value: 'individual',
                  label: (
                    <Group gap="xs">
                      <IconUser size={16} />
                      <span>ИП</span>
                    </Group>
                  ),
                },
              ]}
              fullWidth
              radius="xl"
            />
          </Box>

          <Divider label="Основные реквизиты" labelPosition="left" />

          {/* Название */}
          <TextInput
            label="Название компании/ИП"
            placeholder={formData.entity_type === 'legal' ? 'ООО "Ромашка"' : 'ИП Иванов И.И.'}
            required
            value={formData.company_name}
            onChange={(e) => handleChange('company_name', e.target.value)}
            error={errors.company_name}
          />

          {/* ИНН, КПП, ОГРН */}
          {formData.entity_type === 'legal' ? (
            <Group grow align="flex-start">
              <TextInput
                label="ИНН"
                placeholder="10 цифр"
                required
                value={formData.inn}
                onChange={(e) => handleChange('inn', e.target.value.replace(/\D/g, ''))}
                error={errors.inn}
                rightSection={
                  formData.inn && !errors.inn ? (
                    <IconCheck size={16} color="green" />
                  ) : null
                }
                maxLength={10}
              />
              <TextInput
                label="КПП"
                placeholder="9 цифр"
                required
                value={formData.kpp}
                onChange={(e) => handleChange('kpp', e.target.value.replace(/\D/g, ''))}
                error={errors.kpp}
                rightSection={
                  formData.kpp && !errors.kpp ? (
                    <IconCheck size={16} color="green" />
                  ) : null
                }
                maxLength={9}
              />
              <TextInput
                label="ОГРН"
                placeholder="13 цифр"
                required
                value={formData.ogrn}
                onChange={(e) => handleChange('ogrn', e.target.value.replace(/\D/g, ''))}
                error={errors.ogrn}
                rightSection={
                  formData.ogrn && !errors.ogrn ? (
                    <IconCheck size={16} color="green" />
                  ) : null
                }
                maxLength={13}
              />
            </Group>
          ) : (
            <Group grow align="flex-start">
              <TextInput
                label="ИНН"
                placeholder="12 цифр"
                required
                value={formData.inn}
                onChange={(e) => handleChange('inn', e.target.value.replace(/\D/g, ''))}
                error={errors.inn}
                rightSection={
                  formData.inn && !errors.inn ? (
                    <IconCheck size={16} color="green" />
                  ) : null
                }
                maxLength={12}
              />
              <TextInput
                label="ОГРНИП"
                placeholder="15 цифр"
                required
                value={formData.ogrn}
                onChange={(e) => handleChange('ogrn', e.target.value.replace(/\D/g, ''))}
                error={errors.ogrn}
                rightSection={
                  formData.ogrn && !errors.ogrn ? (
                    <IconCheck size={16} color="green" />
                  ) : null
                }
                maxLength={15}
              />
            </Group>
          )}

          <Divider label="Банковские реквизиты" labelPosition="left" />

          {/* Расчётный счёт */}
          <TextInput
            label="Расчётный счёт"
            placeholder="20 цифр"
            required
            leftSection={<IconBuildingBank size={16} />}
            value={formData.bank_account}
            onChange={(e) => handleChange('bank_account', e.target.value.replace(/\D/g, ''))}
            error={errors.bank_account}
            rightSection={
              formData.bank_account && !errors.bank_account ? (
                <IconCheck size={16} color="green" />
              ) : null
            }
            maxLength={20}
          />

          {/* Банк и БИК */}
          <Group grow>
            <TextInput
              label="Банк"
              placeholder="Название банка"
              required
              value={formData.bank_name}
              onChange={(e) => handleChange('bank_name', e.target.value)}
              error={errors.bank_name}
            />
            <TextInput
              label="БИК"
              placeholder="9 цифр"
              required
              value={formData.bank_bik}
              onChange={(e) => handleChange('bank_bik', e.target.value.replace(/\D/g, ''))}
              error={errors.bank_bik}
              rightSection={
                formData.bank_bik && !errors.bank_bik ? (
                  <IconCheck size={16} color="green" />
                ) : null
              }
              maxLength={9}
            />
          </Group>

          <Divider label="Адреса" labelPosition="left" />

          {/* Юридический адрес */}
          <TextInput
            label="Юридический адрес"
            placeholder="Введите юридический адрес"
            required
            leftSection={<IconMapPin size={16} />}
            value={formData.legal_address}
            onChange={(e) => handleChange('legal_address', e.target.value)}
            error={errors.legal_address}
          />

          {/* Физический адрес */}
          <TextInput
            label="Физический адрес"
            placeholder="Введите физический адрес"
            required
            leftSection={<IconMapPin size={16} />}
            value={formData.physical_address}
            onChange={(e) => handleChange('physical_address', e.target.value)}
            error={errors.physical_address}
          />

          <Divider label="Руководство и контакты" labelPosition="left" />

          {/* Директор */}
          <Select
            label="Генеральный директор / Руководитель"
            placeholder="Выберите из контактов"
            data={directorOptions.length > 0 ? directorOptions : contactOptions}
            value={formData.director_contact_id?.toString() || null}
            onChange={(value) => handleChange('director_contact_id', value ? parseInt(value) : null)}
            clearable
            searchable
          />

          {/* Контактное лицо */}
          <Select
            label="Контактное лицо"
            placeholder="Выберите из контактов"
            data={contactOptions}
            value={formData.contact_person_id?.toString() || null}
            onChange={(value) => handleChange('contact_person_id', value ? parseInt(value) : null)}
            clearable
            searchable
          />

          {/* Телефон и Email контактного лица (нередактируемые) */}
          {selectedContactPerson && (getContactPhone(selectedContactPerson) || selectedContactPerson.email) && (
            <Group gap="xl" ml="xs">
              {getContactPhone(selectedContactPerson) && (
                <Group gap="xs">
                  <IconPhone size={14} color={tokens.colors.neutral[6]} />
                  <Text size="sm" c="dimmed">
                    {getContactPhone(selectedContactPerson)}
                  </Text>
                </Group>
              )}
              {selectedContactPerson.email && (
                <Group gap="xs">
                  <IconMail size={14} color={tokens.colors.neutral[6]} />
                  <Text size="sm" c="dimmed">
                    {selectedContactPerson.email}
                  </Text>
                </Group>
              )}
            </Group>
          )}

          <Divider label="Договор (необязательно)" labelPosition="left" />

          {/* Дата начала договора */}
          <DateInput
            label="Дата начала договора"
            placeholder="Выберите дату"
            leftSection={<IconCalendar size={16} />}
            value={formData.contract_start_date}
            onChange={(value) => handleChange('contract_start_date', value)}
            clearable
            locale="ru"
            valueFormat="DD.MM.YYYY"
          />

          {/* Дни и время занятий */}
          <Group grow>
            <TextInput
              label="Дни занятий"
              placeholder="Пн, Ср, Пт"
              value={formData.class_days}
              onChange={(e) => handleChange('class_days', e.target.value)}
            />
            <TextInput
              label="Время занятий"
              placeholder="10:00-12:00"
              value={formData.class_time}
              onChange={(e) => handleChange('class_time', e.target.value)}
            />
          </Group>

          <Divider label="Настройки" labelPosition="left" />

          {/* Основные реквизиты */}
          <Checkbox
            label="Основные реквизиты"
            description="Будут использоваться по умолчанию"
            checked={formData.is_primary}
            onChange={(e) => handleChange('is_primary', e.currentTarget.checked)}
          />

          {/* Кнопки */}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" loading={loading}>
              {isEditing ? 'Сохранить' : 'Добавить'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default RequisitesFormModal;
